// Authoring-side template.yaml loader + push-body builder (#535). Used by `rekor admin template push`.
// Reads the per-locale slice `source` dir into a file map + the per-locale README + the rekor text.
// The reverse "hubs powered by this backend" link is a curated, stored `relatedChannels` list passed
// through here (server-normalized); see workers/shared/src/templates/contract.ts (#547).

import { existsSync, readFileSync } from 'fs';
import { join, resolve } from 'path';
import { parseYaml } from '../config-as-code/yaml.js';
import { readDirToFileMap } from './files.js';
import {
  TEMPLATE_SLUG_REGEX,
  isTemplateLocale,
  type TemplateLocale,
  type TemplateYaml,
  type TemplatePushBody,
  type TemplatePushLocale,
} from './types.js';

/** Authoring dir for the Templates gallery: `<gitRoot>/rekor-templates/<slug>/`. Sibling of `rekor-ws/`. */
export const TEMPLATES_AUTHORING_SUBDIR = 'rekor-templates';
export function resolveTemplatesDir(gitRoot: string): string {
  return join(gitRoot, TEMPLATES_AUTHORING_SUBDIR);
}

export interface LoadedManifest {
  manifest: TemplateYaml;
  templateDir: string;
}

/** Read + light-validate `rekor-templates/<slug>/template.yaml`. Server-side validation is authoritative. */
export function loadTemplateManifest(gitRoot: string, slug: string): LoadedManifest {
  const templateDir = join(resolveTemplatesDir(gitRoot), slug);
  const manifestPath = join(templateDir, 'template.yaml');
  if (!existsSync(manifestPath)) {
    throw new Error(`No template.yaml at ${manifestPath}. Author it under rekor-templates/${slug}/.`);
  }
  const manifest = parseYaml(readFileSync(manifestPath, 'utf-8')) as TemplateYaml | null;
  if (!manifest || typeof manifest !== 'object') {
    throw new Error(`Invalid template.yaml at ${manifestPath}: not a YAML object.`);
  }
  if (manifest.slug !== slug) {
    throw new Error(`template.yaml slug "${manifest.slug}" does not match folder "${slug}".`);
  }
  if (!TEMPLATE_SLUG_REGEX.test(manifest.slug)) {
    throw new Error(`Invalid slug "${manifest.slug}": lowercase alphanumeric + hyphens, max 63.`);
  }
  if (!isTemplateLocale(manifest.defaultLocale)) {
    throw new Error(`template.yaml defaultLocale must be one of en, pt, es.`);
  }
  if (!manifest.rekor) {
    throw new Error('template.yaml must define a `rekor:` block (the slice).');
  }
  if (!manifest.rekor.locales?.[manifest.defaultLocale]) {
    throw new Error('`rekor.locales` must include the defaultLocale.');
  }
  return { manifest, templateDir };
}

/** Build the admin push body from a loaded manifest, reading each locale's slice + README off disk. */
export function buildPushBody({ manifest, templateDir }: LoadedManifest): TemplatePushBody {
  // loadTemplateManifest guarantees `manifest.rekor` is present (the owning block).
  const rekor = manifest.rekor!;
  const authoredLocales = Object.keys(rekor.locales) as TemplateLocale[];

  const locales: Partial<Record<TemplateLocale, TemplatePushLocale>> = {};
  for (const locale of authoredLocales) {
    const rekorEntry = rekor.locales[locale];

    // rekor slice: read the per-locale `source` dir into a flat file map (the owning block).
    let rekorFiles: Record<string, string> | undefined;
    if (rekorEntry?.source) {
      const sliceDir = resolve(templateDir, rekorEntry.source);
      if (!existsSync(sliceDir)) {
        throw new Error(`rekor source not found for ${locale}: ${rekorEntry.source} (resolved ${sliceDir})`);
      }
      rekorFiles = readDirToFileMap(sliceDir);
    }

    // README ships as raw markdown; the server/website renders it. Per-locale, optional.
    const readmePath = join(templateDir, `README.${locale}.md`);
    const readmeMarkdown = existsSync(readmePath) ? readFileSync(readmePath, 'utf-8') : '';

    locales[locale] = {
      readmeMarkdown,
      ...(rekorFiles ? { rekorFiles } : {}),
      ...(rekorEntry
        ? { rekor: { metaTitle: rekorEntry.metaTitle, metaDescription: rekorEntry.metaDescription ?? '' } }
        : {}),
    };
  }

  return {
    slug: manifest.slug,
    defaultLocale: manifest.defaultLocale,
    common: {
      category: manifest.category,
      // Pass tags through as-authored; the server normalizes (trim/lowercase/dedupe/cap) authoritatively.
      tags: manifest.tags ?? [],
      // Related WayAI channels pass through as-authored (slug or { slug, title }); the server normalizes.
      relatedChannels: manifest.relatedChannels ?? [],
      status: manifest.status,
      order: manifest.order ?? 0,
      // The install command is the rekor one (locale-agnostic).
      ...(rekor.installCommand ? { installCommand: rekor.installCommand } : {}),
    },
    locales,
  };
}
